"use client";

import { useState, useCallback, Fragment } from "react";
import { compareFolders, type SessionListItem, type FolderResponse } from "@/app/lib/types";
import RecentTab from "./RecentTab";
import FoldersTab from "./FoldersTab";
import NewFolderDialog from "../common/NewFolderDialog";

interface SidebarProps {
  sessions: SessionListItem[];
  folders: FolderResponse[];
  activeSessionId?: string;
  onSessionClick: (id: string) => void;
  onSessionDelete: (id: string) => void;
  onSessionRename: (id: string, title: string) => void;
  onSessionFlag: (id: string) => void;
  onNewChatInFolder: (folderId: string) => void;
  onCreateFolder: () => void;
  onRenameFolder: (id: string, name: string) => void;
  onSetFolderSeparator?: (id: string, value: boolean) => void;
  onDeleteFolder: (id: string) => void;
  onReorderFolders?: (orderedIds: string[]) => void;
  onSettingsClick?: () => void;
  onMoveToFolder?: (id: string) => void;
  onMoveSessionToFolder?: (sessionId: string, folderId: string) => void;
  onMoveToNewFolder?: (sessionId: string) => void;
  onShare?: (id: string) => void;
  currentUserEmail?: string;
  isLoading?: boolean;
  searchQuery?: string;
  searchResults?: { title_matches: Array<{id: string; title: string; updated_at: string}>; content_matches: Array<{id: string; title: string; updated_at: string; snippet: string}> } | null;
  isSearching?: boolean;
  onBulkDelete?: (ids: string[]) => Promise<void>;
  onBulkMoveToFolder?: (ids: string[], folderId: string) => Promise<void>;
  onBulkMoveToNewFolder?: (ids: string[], name: string) => Promise<void>;
  newlyCreatedFolderId?: string | null;
  onAutoEditConsumed?: () => void;
}

type Tab = "recent" | "folders";

export default function Sidebar({
  sessions,
  folders,
  activeSessionId,
  onSessionClick,
  onSessionDelete,
  onSessionRename,
  onSessionFlag,
  onNewChatInFolder,
  onCreateFolder,
  onRenameFolder,
  onSetFolderSeparator,
  onDeleteFolder,
  onReorderFolders,
  onSettingsClick,
  onMoveToFolder,
  onMoveSessionToFolder,
  onMoveToNewFolder,
  onShare,
  currentUserEmail,
  isLoading,
  searchQuery,
  searchResults,
  isSearching,
  onBulkDelete,
  onBulkMoveToFolder,
  onBulkMoveToNewFolder,
  newlyCreatedFolderId,
  onAutoEditConsumed,
}: SidebarProps) {
  const [activeTab, setActiveTab] = useState<Tab>("recent");
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set());
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const handleToggleSelect = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const handleCancelSelection = useCallback(() => {
    setSelectionMode(false);
    setSelectedIds(new Set());
  }, []);

  const handleDeleteSelected = useCallback(async () => {
    if (selectedIds.size === 0) return;
    setShowDeleteConfirm(true);
  }, [selectedIds]);

  const confirmDelete = useCallback(async () => {
    setShowDeleteConfirm(false);
    if (onBulkDelete) {
      await onBulkDelete(Array.from(selectedIds));
    }
    setSelectedIds(new Set());
    setSelectionMode(false);
  }, [selectedIds, onBulkDelete]);

  const [showBulkMoveFolder, setShowBulkMoveFolder] = useState(false);
  const [showNewFolderDialog, setShowNewFolderDialog] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const handleToggleFolder = useCallback((id: string) => {
    setExpandedFolders((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  return (
    <div className="sidebar">
      {/* Header: tabs */}
      <div
        style={{
          padding: "8px 12px 8px",
          display: "flex",
          alignItems: "center",
          gap: "8px",
          borderBottom: "1px solid var(--border)",
        }}
      >
        {/* Segmented control */}
        <div
          style={{
            display: "flex",
            flex: 1,
            background: "var(--bg)",
            borderRadius: "var(--radius-md)",
            padding: "2px",
            gap: "2px",
          }}
        >
          <button
            onClick={() => setActiveTab("recent")}
            style={{
              flex: 1,
              padding: "6px 0",
              border: "none",
              borderRadius: "var(--radius-sm)",
              background:
                activeTab === "recent" ? "var(--surface-hover)" : "transparent",
              color:
                activeTab === "recent"
                  ? "var(--text-primary)"
                  : "var(--text-secondary)",
              fontSize: "13px",
              fontWeight: 500,
              cursor: "pointer",
              transition: "all var(--transition-fast)",
            }}
          >
            Recent
          </button>
          <button
            onClick={() => setActiveTab("folders")}
            style={{
              flex: 1,
              padding: "6px 0",
              border: "none",
              borderRadius: "var(--radius-sm)",
              background:
                activeTab === "folders" ? "var(--surface-hover)" : "transparent",
              color:
                activeTab === "folders"
                  ? "var(--text-primary)"
                  : "var(--text-secondary)",
              fontSize: "13px",
              fontWeight: 500,
              cursor: "pointer",
              transition: "all var(--transition-fast)",
            }}
          >
            Folders
          </button>
        </div>
        {/* Select / Cancel button */}
        <button
          onClick={selectionMode ? handleCancelSelection : () => setSelectionMode(true)}
          style={{
            background: "none",
            border: "none",
            color: selectionMode ? "var(--error)" : "var(--text-secondary)",
            fontSize: "12px",
            cursor: "pointer",
            padding: "4px 6px",
            flexShrink: 0,
            whiteSpace: "nowrap",
          }}
        >
          {selectionMode ? (
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
              <path d="M4 4l8 8M12 4l-8 8" />
            </svg>
          ) : (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="3" width="7" height="7" rx="1" />
              <path d="M5 6.5l1.5 1.5L9 5" />
              <rect x="3" y="14" width="7" height="7" rx="1" />
              <rect x="14" y="3" width="7" height="2" rx="0.5" />
              <rect x="14" y="8" width="7" height="2" rx="0.5" />
              <rect x="14" y="14" width="7" height="2" rx="0.5" />
              <rect x="14" y="19" width="7" height="2" rx="0.5" />
            </svg>
          )}
        </button>
      </div>

      {/* Scrollable content */}
      <div style={{ flex: 1, overflowY: "auto", overflowX: "hidden" }}>
        {searchQuery ? (
          <div style={{ padding: "8px 0" }}>
            {isSearching && (
              <div style={{ padding: "12px", textAlign: "center", color: "var(--text-tertiary)", fontSize: "12px" }}>Searching...</div>
            )}
            {searchResults && !isSearching && (
              <>
                {searchResults.title_matches.length === 0 && searchResults.content_matches.length === 0 && (
                  <div style={{ padding: "16px 12px", textAlign: "center", color: "var(--text-tertiary)", fontSize: "12px" }}>No results found</div>
                )}
                {searchResults.title_matches.map((match) => (
                  <div
                    key={match.id}
                    className={`session-item ${activeSessionId === match.id ? "session-item-active" : ""}`}
                    onClick={() => onSessionClick(match.id)}
                    style={{ cursor: "pointer" }}
                  >
                    <span style={{ fontSize: "13px", fontWeight: 500, color: "var(--text-primary)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{match.title}</span>
                  </div>
                ))}
                {searchResults.content_matches.length > 0 && (
                  <>
                    <div style={{ borderTop: "1px solid var(--border)", margin: "8px 12px", paddingTop: "8px", fontSize: "10px", color: "var(--text-tertiary)", textTransform: "uppercase", letterSpacing: "0.05em" }}>In responses</div>
                    {searchResults.content_matches.map((match) => (
                      <div
                        key={match.id}
                        className={`session-item ${activeSessionId === match.id ? "session-item-active" : ""}`}
                        onClick={() => onSessionClick(match.id)}
                        style={{ cursor: "pointer" }}
                      >
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontSize: "13px", fontWeight: 500, color: "var(--text-primary)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{match.title}</div>
                          <div style={{ fontSize: "11px", color: "var(--text-tertiary)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{match.snippet}</div>
                        </div>
                      </div>
                    ))}
                  </>
                )}
              </>
            )}
          </div>
        ) : isLoading ? (
          <div style={{ display: "flex", justifyContent: "center", padding: "24px 0" }}>
            <div className="spinner" />
          </div>
        ) : activeTab === "recent" ? (
          <RecentTab
            sessions={sessions}
            folders={folders}
            activeSessionId={activeSessionId}
            onSessionClick={onSessionClick}
            onSessionDelete={onSessionDelete}
            onSessionRename={onSessionRename}
            onSessionFlag={onSessionFlag}
            onMoveToFolder={onMoveToFolder}
            onMoveSessionToFolder={onMoveSessionToFolder}
            onMoveToNewFolder={onMoveToNewFolder}
            onShare={onShare}
            currentUserEmail={currentUserEmail}
            selectionMode={selectionMode}
            selectedIds={selectedIds}
            onToggleSelect={handleToggleSelect}
          />
        ) : (
          <FoldersTab
            folders={folders}
            sessions={sessions}
            activeSessionId={activeSessionId}
            expandedFolders={expandedFolders}
            onToggleFolder={handleToggleFolder}
            onSessionClick={onSessionClick}
            onSessionDelete={onSessionDelete}
            onSessionRename={onSessionRename}
            onSessionFlag={onSessionFlag}
            onMoveToFolder={onMoveToFolder}
            onMoveSessionToFolder={onMoveSessionToFolder}
            onMoveToNewFolder={onMoveToNewFolder}
            onShare={onShare}
            onNewChatInFolder={onNewChatInFolder}
            onRenameFolder={onRenameFolder}
            onSetFolderSeparator={onSetFolderSeparator}
            onDeleteFolder={onDeleteFolder}
            onReorderFolders={onReorderFolders}
            onCreateFolder={onCreateFolder}
            currentUserEmail={currentUserEmail}
            selectionMode={selectionMode}
            selectedIds={selectedIds}
            onToggleSelect={handleToggleSelect}
            newlyCreatedFolderId={newlyCreatedFolderId}
            onAutoEditConsumed={onAutoEditConsumed}
          />
        )}
      </div>

      {/* Bulk action buttons */}
      {selectionMode && selectedIds.size > 0 && (
        <div style={{ padding: "8px 12px", borderTop: "1px solid var(--border)", display: "flex", gap: "8px" }}>
          <button
            onClick={() => setShowBulkMoveFolder(true)}
            style={{
              flex: 1,
              padding: "8px 0",
              background: "var(--surface-hover)",
              color: "var(--text)",
              border: "1px solid var(--border)",
              borderRadius: "var(--radius-md)",
              fontSize: "13px",
              fontWeight: 500,
              cursor: "pointer",
            }}
          >
            Move ({selectedIds.size})
          </button>
          <button
            onClick={handleDeleteSelected}
            style={{
              flex: 1,
              padding: "8px 0",
              background: "var(--error)",
              color: "white",
              border: "none",
              borderRadius: "var(--radius-md)",
              fontSize: "13px",
              fontWeight: 500,
              cursor: "pointer",
            }}
          >
            Delete ({selectedIds.size})
          </button>
        </div>
      )}

      {/* Bulk move folder picker */}
      {showBulkMoveFolder && (
        <>
        <div onClick={() => setShowBulkMoveFolder(false)} style={{ position: "fixed", inset: 0, zIndex: 49 }} />
        <div style={{
          position: "absolute", bottom: "50px", left: "12px",
          width: "160px",
          background: "var(--surface)", border: "1px solid var(--border)",
          borderRadius: "var(--radius-lg)", padding: "4px", zIndex: 50,
          boxShadow: "0 4px 16px rgba(0,0,0,0.4)",
          maxHeight: "400px", overflowY: "auto",
        }}>
          <button
            className="btn-ghost"
            style={{ width: "100%", justifyContent: "flex-start", padding: "6px 10px", fontSize: "13px", fontWeight: 600, color: "var(--accent, var(--text))" }}
            onClick={() => { setShowBulkMoveFolder(false); setShowNewFolderDialog(true); }}
          >
            + New Folder…
          </button>
          <div style={{ height: "1px", background: "var(--border)", margin: "4px 0" }} />
          {[...folders].sort(compareFolders).map((folder) => (
            <Fragment key={folder.id}>
              {folder.separator_before && <div style={{ height: "2px", background: "#484848", borderRadius: "1px", margin: "4px 6px" }} />}
              <button
                className="btn-ghost"
                style={{ width: "100%", justifyContent: "flex-start", padding: "6px 10px", fontSize: "13px" }}
                onClick={async () => {
                  if (onBulkMoveToFolder) {
                    await onBulkMoveToFolder(Array.from(selectedIds), folder.id);
                  }
                  setShowBulkMoveFolder(false);
                  setSelectedIds(new Set());
                  setSelectionMode(false);
                }}
              >
                {folder.name}
              </button>
            </Fragment>
          ))}
        </div>
        </>
      )}

      <NewFolderDialog
        isOpen={showNewFolderDialog}
        onClose={() => setShowNewFolderDialog(false)}
        onCreate={async (name) => {
          const ids = Array.from(selectedIds);
          if (onBulkMoveToNewFolder) await onBulkMoveToNewFolder(ids, name);
          setSelectedIds(new Set());
          setSelectionMode(false);
        }}
      />

      {/* Delete confirmation dialog */}
      {showDeleteConfirm && (
        <>
          <div onClick={() => setShowDeleteConfirm(false)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 99 }} />
          <div style={{
            position: "fixed", top: "50%", left: "50%", transform: "translate(-50%, -50%)",
            background: "var(--surface)", border: "1px solid var(--border)",
            borderRadius: "var(--radius-lg)", padding: "20px", zIndex: 100,
            minWidth: "240px", textAlign: "center",
          }}>
            <p style={{ margin: "0 0 16px", fontSize: "14px", color: "var(--text)" }}>
              Delete {selectedIds.size} item{selectedIds.size !== 1 ? "s" : ""}?
            </p>
            <div style={{ display: "flex", gap: "8px", justifyContent: "center" }}>
              <button onClick={() => setShowDeleteConfirm(false)} style={{ padding: "6px 16px", background: "var(--surface-hover)", color: "var(--text)", border: "1px solid var(--border)", borderRadius: "var(--radius-md)", fontSize: "13px", cursor: "pointer" }}>Cancel</button>
              <button onClick={confirmDelete} style={{ padding: "6px 16px", background: "var(--error)", color: "white", border: "none", borderRadius: "var(--radius-md)", fontSize: "13px", cursor: "pointer" }}>Delete</button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
